import { useEffect, useMemo, useState } from 'react';
import { Modal } from 'antd';
import { useTranslate } from '@tolgee/react';
import { openExternalURL } from 'utils/openExternalURL';
import chatttSDK from 'services/chattt';
import { logger } from 'services/logger';
import { onceLoginRedirect, type LoginVersionMismatch } from './loginVersion';
import { isStoreAuthLogin, resolveStoreAuthQuickLogin } from './legacyStoreAuthRedirect';

export default function LoginVersionNotice({
  data,
  nextpage,
  onClose,
}: {
  data: LoginVersionMismatch;
  nextpage?: string;
  onClose: () => void;
}) {
  const { t } = useTranslate();
  const [seconds, setSeconds] = useState(10);
  const redirect = useMemo(
    () =>
      onceLoginRedirect(() => {
        if (data.action === 'DOWNLOAD' || data.action === 'OPEN_WEB') {
          const externalUrl = data.action === 'DOWNLOAD' ? data.downloadUrl : data.redirectUrl;
          if (chatttSDK?.openExternal) {
            void chatttSDK.openExternal(externalUrl).catch(() => {
              logger.warn('[LoginVersionNotice] system browser unavailable; opening web fallback');
              void openExternalURL(externalUrl);
            });
          } else {
            void openExternalURL(externalUrl);
          }
          return;
        }
        let target = data.redirectUrl;
        if (data.targetApp === 'PERSEUS' && isStoreAuthLogin(nextpage)) {
          const url = new URL(target);
          target = resolveStoreAuthQuickLogin(
            `${url.origin}/login`,
            nextpage,
            url.searchParams.get('token') || '',
          );
        }
        window.location.replace(target);
      }),
    [data, nextpage],
  );

  useEffect(() => {
    if (data.action !== 'REDIRECT') return;
    const deadline = Date.now() + 10_000;
    const timer = window.setInterval(() => {
      const remaining = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      setSeconds(remaining);
      if (remaining === 0) redirect();
    }, 250);
    return () => window.clearInterval(timer);
  }, [data, redirect]);

  const edition =
    data.targetApp === 'FETT'
      ? t('loginVersion.classic', { defaultValue: 'Classic Edition' })
      : t('loginVersion.international', { defaultValue: 'International Edition' });
  let buttonText = t('loginVersion.goNow', { defaultValue: 'Go now' });
  let description = t('loginVersion.redirect', {
    edition,
    seconds,
    defaultValue: 'This account belongs to {edition}. Redirecting in {seconds} seconds.',
  });
  if (data.action === 'DOWNLOAD') {
    buttonText = t('loginVersion.goDownload', { defaultValue: 'Go to download page' });
    description = t('loginVersion.download', {
      edition,
      defaultValue: 'This account belongs to {edition}. Please download the corresponding client.',
    });
  } else if (data.action === 'OPEN_WEB') {
    buttonText = t('loginVersion.goWeb', { defaultValue: 'Open web version' });
    description = t('loginVersion.openWeb', {
      edition,
      defaultValue: 'This account belongs to {edition}. Please use the web version.',
    });
  }
  return (
    <Modal
      visible
      title={t('loginVersion.title', { defaultValue: 'Account version mismatch' })}
      onCancel={onClose}
      onOk={redirect}
      cancelButtonProps={{ style: { display: 'none' } }}
      okText={buttonText}
    >
      {description}
    </Modal>
  );
}
